/**
 * Payments Service
 * Mercado Pago Checkout Pro (BRL)
 */

import { API_ENDPOINTS } from '@/constants/api';
import { apiClient, ApiResponse } from './apiClient';

export type PaymentType =
  | 'APPOINTMENT_BOOKING'
  | 'ON_DEMAND_TELEMEDICINE'
  | 'ON_DEMAND_HOME_CARE';

export interface CreatePreferenceRequest {
  type: PaymentType;
  doctorId?: string;
  serviceType?: 'video' | 'home' | 'clinic';
  appointmentDate?: string;
  appointmentTime?: string;
}

export interface PreferenceResponse {
  paymentId: string;
  preferenceId: string;
  initPoint: string;
  amount: number;
  currency: string;
  type: PaymentType;
}

export interface PaymentStatusResponse {
  id: string;
  status: 'pending' | 'succeeded' | 'failed' | 'cancelled';
  amount: number;
  currency: string;
  type: PaymentType;
  appointmentId?: string;
  onDemandQueueId?: string;
  initPoint?: string;
  statusDetail?: string | null;
  statusDetailLabel?: string | null;
}

export interface MercadoPagoIntegrationStatus {
  configured: boolean;
  environment: string;
  accessTokenPresent: boolean;
}

export async function getPaymentsStatus(): Promise<ApiResponse<MercadoPagoIntegrationStatus>> {
  return apiClient.get(API_ENDPOINTS.PAYMENTS.STATUS);
}

export async function createPreference(
  data: CreatePreferenceRequest,
): Promise<ApiResponse<PreferenceResponse>> {
  return apiClient.post(API_ENDPOINTS.PAYMENTS.PREFERENCE, data);
}

export async function getPaymentById(
  paymentId: string,
  mpPaymentId?: string | null,
): Promise<ApiResponse<PaymentStatusResponse>> {
  const query = mpPaymentId
    ? `?mpPaymentId=${encodeURIComponent(mpPaymentId)}`
    : '';
  return apiClient.get(`${API_ENDPOINTS.PAYMENTS.GET(paymentId)}${query}`);
}

const MP_INIT_POINT_KEY = 'mp_checkout_init_point';
const MP_LAST_PAYMENT_ID_KEY = 'mp_last_payment_id';

export function persistMercadoPagoCheckoutContext(paymentId: string, initPoint: string) {
  sessionStorage.setItem(MP_INIT_POINT_KEY, initPoint);
  sessionStorage.setItem(MP_LAST_PAYMENT_ID_KEY, paymentId);
}

export function readMercadoPagoInitPoint(): string | null {
  return sessionStorage.getItem(MP_INIT_POINT_KEY);
}

export function readLastPaymentId(): string | null {
  return sessionStorage.getItem(MP_LAST_PAYMENT_ID_KEY);
}

/** Abre o checkout em outra aba no gesto do clique (evita bloqueio de popup). */
export function prepareMercadoPagoWindow(): Window | null {
  try {
    return window.open('about:blank', 'mercadopago_checkout');
  } catch {
    return null;
  }
}

/**
 * Mantém o Dr. House na tela de resultado (polling) e abre o Mercado Pago
 * numa aba à parte. Pix não faz auto_return — se a aba única for o checkout, o usuário fica preso.
 */
export function launchMercadoPagoCheckout(params: {
  checkoutWindow: Window | null;
  initPoint: string;
  paymentId: string;
  goToResult: (path: string) => void;
}) {
  persistMercadoPagoCheckoutContext(params.paymentId, params.initPoint);
  const resultPath = `/payment/result?external_reference=${encodeURIComponent(params.paymentId)}`;

  if (params.checkoutWindow && !params.checkoutWindow.closed) {
    params.checkoutWindow.location.replace(params.initPoint);
  }

  params.goToResult(resultPath);
}

/** Preços (centavos) — espelham o backend */
export const PAYMENT_PRICING = {
  ON_DEMAND_TELEMEDICINE_CENTS: 100,
  ON_DEMAND_HOME_CARE_CENTS: 100,
  /** Agendamento telemedicina (não plantão) — valor de teste */
  APPOINTMENT_TELEMEDICINE_CENTS: 100,
  SERVICE_FEES: {
    video: 0,
    home: 0,
    clinic: 0,
  } as const,
};
